import {
  Bell,
  Bot,
  Box,
  Braces,
  Calendar,
  ChartNoAxesCombined,
  CircleAlert,
  CirclePlus,
  CircleDollarSign,
  Clock,
  Cloud,
  Code2,
  Contact,
  CreditCard,
  Database,
  Download,
  FileText,
  Filter,
  Flag,
  GitBranch,
  Globe,
  Link,
  LockKeyhole,
  Mail,
  MessageSquare,
  Package,
  Search,
  Send,
  ShieldCheck,
  ShoppingCart,
  Sheet,
  Repeat2,
  RefreshCw,
  Trash2,
  Upload,
  User,
  Users,
  Workflow,
  Wrench,
  Zap,
  type LucideIcon,
} from "lucide-react";

import type { BuilderNodeType } from "@/shared/builder-session";

interface WorkflowVisual {
  icon: LucideIcon;
  iconClassName: string;
  dotClassName: string;
  ringClassName: string;
}

const visuals = {
  trigger: {
    icon: Zap,
    iconClassName: "border-chart-2/30 bg-chart-2/10 text-chart-2",
    dotClassName: "bg-chart-2",
    ringClassName: "border-chart-2/40",
  },
  lookup: {
    icon: Database,
    iconClassName: "border-primary/30 bg-primary/10 text-primary",
    dotClassName: "bg-primary",
    ringClassName: "border-primary/35",
  },
  condition: {
    icon: GitBranch,
    iconClassName: "border-chart-3/30 bg-chart-3/10 text-chart-3",
    dotClassName: "bg-chart-3",
    ringClassName: "border-chart-3/40",
  },
  mapping: {
    icon: Workflow,
    iconClassName: "border-chart-4/35 bg-chart-4/15 text-chart-4",
    dotClassName: "bg-chart-4",
    ringClassName: "border-chart-4/40",
  },
  document: {
    icon: FileText,
    iconClassName: "border-primary/30 bg-primary/10 text-primary",
    dotClassName: "bg-primary",
    ringClassName: "border-primary/35",
  },
  notification: {
    icon: Bell,
    iconClassName: "border-success/30 bg-success/10 text-success",
    dotClassName: "bg-success",
    ringClassName: "border-success/40",
  },
  error: {
    icon: CircleAlert,
    iconClassName: "border-destructive/30 bg-destructive/10 text-destructive",
    dotClassName: "bg-destructive",
    ringClassName: "border-destructive/40",
  },
  loop: {
    icon: Repeat2,
    iconClassName: "border-chart-5/30 bg-chart-5/10 text-chart-5",
    dotClassName: "bg-chart-5",
    ringClassName: "border-chart-5/40",
  },
  end: {
    icon: Flag,
    iconClassName: "border-success/30 bg-success/10 text-success",
    dotClassName: "bg-success",
    ringClassName: "border-success/40",
  },
  code: {
    icon: Code2,
    iconClassName: "border-warning/40 bg-warning/15 text-warning-foreground",
    dotClassName: "bg-warning",
    ringClassName: "border-warning/50",
  },
  tool: {
    icon: Wrench,
    iconClassName: "border-primary/30 bg-primary/10 text-primary",
    dotClassName: "bg-primary",
    ringClassName: "border-primary/35",
  },
} satisfies Record<string, WorkflowVisual>;

const iconChoices: Record<string, { icon: LucideIcon; visual: keyof typeof visuals }> = {
  trigger: { icon: Zap, visual: "trigger" },
  webhook: { icon: Zap, visual: "trigger" },
  database: { icon: Database, visual: "lookup" },
  search: { icon: Search, visual: "lookup" },
  lookup: { icon: Search, visual: "lookup" },
  filter: { icon: Filter, visual: "condition" },
  branch: { icon: GitBranch, visual: "condition" },
  condition: { icon: GitBranch, visual: "condition" },
  workflow: { icon: Workflow, visual: "mapping" },
  mapping: { icon: Workflow, visual: "mapping" },
  transform: { icon: Workflow, visual: "mapping" },
  invoice: { icon: FileText, visual: "document" },
  document: { icon: FileText, visual: "document" },
  file: { icon: FileText, visual: "document" },
  spreadsheet: { icon: Sheet, visual: "document" },
  email: { icon: Mail, visual: "notification" },
  send: { icon: Send, visual: "notification" },
  notification: { icon: Bell, visual: "notification" },
  message: { icon: MessageSquare, visual: "notification" },
  user: { icon: User, visual: "lookup" },
  customer: { icon: Contact, visual: "lookup" },
  users: { icon: Users, visual: "lookup" },
  payment: { icon: CreditCard, visual: "document" },
  revenue: { icon: CircleDollarSign, visual: "document" },
  cart: { icon: ShoppingCart, visual: "document" },
  calendar: { icon: Calendar, visual: "trigger" },
  clock: { icon: Clock, visual: "trigger" },
  cloud: { icon: Cloud, visual: "tool" },
  api: { icon: Braces, visual: "tool" },
  integration: { icon: Link, visual: "tool" },
  globe: { icon: Globe, visual: "tool" },
  package: { icon: Package, visual: "tool" },
  box: { icon: Box, visual: "tool" },
  upload: { icon: Upload, visual: "tool" },
  download: { icon: Download, visual: "tool" },
  lock: { icon: LockKeyhole, visual: "condition" },
  security: { icon: ShieldCheck, visual: "condition" },
  analytics: { icon: ChartNoAxesCombined, visual: "mapping" },
  alert: { icon: CircleAlert, visual: "error" },
  error: { icon: CircleAlert, visual: "error" },
  retry: { icon: Repeat2, visual: "error" },
  loop: { icon: Repeat2, visual: "loop" },
  end: { icon: Flag, visual: "end" },
  complete: { icon: Flag, visual: "end" },
  stop: { icon: Flag, visual: "end" },
  code: { icon: Code2, visual: "code" },
  bot: { icon: Bot, visual: "tool" },
  tool: { icon: Wrench, visual: "tool" },
  create: { icon: CirclePlus, visual: "trigger" },
  update: { icon: RefreshCw, visual: "mapping" },
  delete: { icon: Trash2, visual: "error" },
  validate: { icon: ShieldCheck, visual: "condition" },
  return: { icon: Send, visual: "notification" },
  extract: { icon: Filter, visual: "mapping" },
};

/** Keys the LLM may emit as cell `icon` / plan `iconKey` (same catalog as canvas). */
export const WORKFLOW_ICON_KEYS = Object.keys(iconChoices);

export function isWorkflowIconKey(value: string): boolean {
  return Object.prototype.hasOwnProperty.call(iconChoices, value.toLowerCase());
}

function visualForChoice(key: string): WorkflowVisual | undefined {
  const chosen = iconChoices[key];
  return chosen ? { ...visuals[chosen.visual], icon: chosen.icon } : undefined;
}

export function getWorkflowVisual(
  text: string,
  nodeType?: BuilderNodeType | "tool" | "code",
  iconKey?: string,
): WorkflowVisual {
  const chosen = iconKey ? visualForChoice(iconKey.toLowerCase()) : undefined;
  if (chosen) return chosen;

  const value = text.toLowerCase();

  if (nodeType === "trigger") return visuals.trigger;
  if (nodeType === "notification") return visuals.notification;
  if (nodeType === "action") return visuals.tool;

  // Match the primary action before falling back to the broad node type. This
  // prevents incidental words such as "branch" or "if present" from making
  // several otherwise different steps use the same condition icon.
  if (/\b(search|find|lookup|look up|query)\b/.test(value)) {
    return visualForChoice("search")!;
  }
  if (/\b(filter|narrow|screen)\b/.test(value)) return visualForChoice("filter")!;
  if (/\b(extract|select|pick|parse)\b/.test(value)) return visualForChoice("extract")!;
  if (/\b(return|respond|output|emit)\b/.test(value)) return visualForChoice("return")!;
  if (/\b(create|add|generate|insert)\b/.test(value)) return visualForChoice("create")!;
  if (/\b(update|sync|modify|change)\b/.test(value)) return visualForChoice("update")!;
  if (/\b(delete|remove|discard)\b/.test(value)) return visualForChoice("delete")!;
  if (/\b(validate|verify|confirm)\b/.test(value)) return visualForChoice("validate")!;
  if (/\b(trigger|webhook|event|starts?|when|new record)\b/.test(value)) return visuals.trigger;
  if (/\b(map|mapping|transform|normalize|field)\b/.test(value)) return visuals.mapping;
  if (/\b(notify|notification|slack|email|message|alert sales)\b/.test(value)) {
    return visuals.notification;
  }
  if (/\b(error|retry|failure|fallback|exception)\b/.test(value)) return visuals.error;
  if (/\b(invoice|document|file|pdf|receipt)\b/.test(value)) return visuals.document;
  if (/\b(fetch|retrieve|get|download)\b/.test(value)) {
    return visualForChoice("download")!;
  }
  if (/\b(record|customer|account|database)\b/.test(value)) {
    return visualForChoice("database")!;
  }
  if (nodeType === "loop" || /\b(loop|iterate|for each|each item)\b/.test(value)) {
    return visuals.loop;
  }
  if (nodeType === "end") {
    return visuals.end;
  }
  if (
    nodeType === "javascript" ||
    nodeType === "code" ||
    /\b(code|javascript|script|function)\b/.test(value)
  ) {
    return visuals.code;
  }
  if (nodeType === "condition" || /\b(check|condition|exists|decision|branch)\b/.test(value)) {
    return visuals.condition;
  }
  return visuals.tool;
}
